import { useGSCCannibalization, useCannibalizationSummary } from '@/hooks/useGSCCannibalization';
import { Loader2, AlertTriangle, AlertCircle, Info, ChevronDown, ChevronRight, ExternalLink, Copy, Scissors } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

const SEVERITY_CONFIG = {
  high: {
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    icon: AlertTriangle,
    label: 'High Priority',
  },
  medium: {
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    icon: AlertCircle,
    label: 'Medium Priority',
  },
  low: {
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    icon: Info,
    label: 'Low Priority',
  },
};

export function CannibalizationView() {
  const { data: issues, isLoading, error } = useGSCCannibalization();
  const summary = useCannibalizationSummary();
  const [expandedQuery, setExpandedQuery] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  const filteredIssues = issues?.filter(issue =>
    severityFilter === 'all' || issue.severity === severityFilter
  ) || [];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Analyzing keyword cannibalization...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-3" />
          <p className="text-sm text-destructive">Failed to load data</p>
        </div>
      </div>
    );
  }

  if (!issues) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <Scissors className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Search Console Data</h3>
          <p className="text-sm text-muted-foreground">
            Connect a Google Search Console property to detect keyword cannibalization issues.
          </p>
        </div>
      </div>
    );
  }

  if (issues.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center max-w-md">
          <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <Scissors className="h-6 w-6 text-green-500" />
          </div>
          <h3 className="font-semibold mb-2 text-green-500">No Cannibalization Detected</h3>
          <p className="text-sm text-muted-foreground">
            Great! Your pages are not competing for the same keywords.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-4 gap-4 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Scissors className="h-5 w-5 text-primary" />
            SEO Cannibalization Detector
          </h2>
          <p className="text-sm text-muted-foreground">
            Keywords ranking with multiple pages (Last 28 days)
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-5 gap-3">
          <div className="glass-panel p-3 rounded-lg">
            <div className="text-2xl font-bold">{summary.totalIssues}</div>
            <div className="text-xs text-muted-foreground">Total Issues</div>
          </div>
          <div className={cn("glass-panel p-3 rounded-lg", SEVERITY_CONFIG.high.bgColor)}>
            <div className={cn("text-2xl font-bold", SEVERITY_CONFIG.high.color)}>{summary.highSeverity}</div>
            <div className="text-xs text-muted-foreground">High Priority</div>
          </div>
          <div className={cn("glass-panel p-3 rounded-lg", SEVERITY_CONFIG.medium.bgColor)}>
            <div className={cn("text-2xl font-bold", SEVERITY_CONFIG.medium.color)}>{summary.mediumSeverity}</div>
            <div className="text-xs text-muted-foreground">Medium</div>
          </div>
          <div className={cn("glass-panel p-3 rounded-lg", SEVERITY_CONFIG.low.bgColor)}>
            <div className={cn("text-2xl font-bold", SEVERITY_CONFIG.low.color)}>{summary.lowSeverity}</div>
            <div className="text-xs text-muted-foreground">Low</div>
          </div>
          <div className="glass-panel p-3 rounded-lg">
            <div className="text-2xl font-bold text-amber-500">~{summary.totalPotentialLoss}</div>
            <div className="text-xs text-muted-foreground">Est. Click Loss</div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2">
        {(['all', 'high', 'medium', 'low'] as const).map((filter) => (
          <button
            key={filter}
            onClick={() => setSeverityFilter(filter)}
            className={cn(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              severityFilter === filter
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            )}
          >
            {filter === 'all' ? 'All' : SEVERITY_CONFIG[filter].label}
            <span className="ml-1.5 opacity-70">
              ({filter === 'all' ? issues.length : issues.filter(i => i.severity === filter).length})
            </span>
          </button>
        ))}
      </div>

      {/* Issues List */}
      <div className="flex-1 overflow-auto space-y-2">
        {filteredIssues.map((issue) => {
          const isExpanded = expandedQuery === issue.query;
          const config = SEVERITY_CONFIG[issue.severity];
          const Icon = config.icon;

          return (
            <div
              key={issue.query}
              className={cn(
                "glass-panel rounded-lg border overflow-hidden",
                config.borderColor
              )}
            >
              {/* Issue Header */}
              <button
                onClick={() => setExpandedQuery(isExpanded ? null : issue.query)}
                className="w-full p-3 flex items-center gap-3 text-left hover:bg-muted/30 transition-colors"
              >
                <div className={cn("p-1.5 rounded", config.bgColor)}>
                  <Icon className={cn("h-4 w-4", config.color)} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{issue.query}</span>
                    <span className={cn("text-xs px-1.5 py-0.5 rounded", config.bgColor, config.color)}>
                      {issue.pages.length} pages
                    </span>
                  </div>
                  <div className="flex gap-4 text-xs text-muted-foreground mt-0.5">
                    <span>{issue.totalClicks.toLocaleString()} clicks</span>
                    <span>{issue.totalImpressions.toLocaleString()} impressions</span>
                    <span>Avg pos: {issue.avgPosition.toFixed(1)}</span>
                  </div>
                </div>

                {issue.potentialClickLoss > 0 && (
                  <div className="text-right">
                    <div className="text-sm font-medium text-amber-500">
                      ~{issue.potentialClickLoss} clicks
                    </div>
                    <div className="text-xs text-muted-foreground">potential loss</div>
                  </div>
                )}

                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {/* Expanded Pages */}
              {isExpanded && (
                <div className="border-t border-border bg-muted/20">
                  <div className="p-3">
                    <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                      Competing Pages
                    </div>
                    <div className="space-y-2">
                      {issue.pages.map((page, index) => (
                        <div
                          key={page.page}
                          className={cn(
                            "flex items-center gap-3 p-2 rounded-md",
                            index === 0 ? "bg-green-500/10 border border-green-500/20" : "bg-muted/30"
                          )}
                        >
                          <div className={cn(
                            "text-xs font-bold w-6 h-6 flex items-center justify-center rounded",
                            index === 0 ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
                          )}>
                            {index + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm truncate">
                                {getPathFromUrl(page.page)}
                              </span>
                              {index === 0 && (
                                <span className="text-xs text-green-500 font-medium">Best performer</span>
                              )}
                            </div>
                            <div className="flex gap-4 text-xs text-muted-foreground mt-0.5">
                              <span>{page.clicks} clicks</span>
                              <span>{page.impressions} imp</span>
                              <span>CTR: {page.ctr.toFixed(1)}%</span>
                              <span>Pos: {page.position.toFixed(1)}</span>
                            </div>
                          </div>

                          <div className="flex gap-1">
                            <button
                              onClick={() => navigator.clipboard.writeText(page.page)}
                              className="p-1.5 rounded hover:bg-muted transition-colors"
                              title="Copy URL"
                            >
                              <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => window.open(page.page, '_blank')}
                              className="p-1.5 rounded hover:bg-muted transition-colors"
                              title="Open page"
                            >
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Recommendation */}
                    <div className="mt-3 p-2 rounded-md bg-primary/5 border border-primary/20 text-xs">
                      <span className="font-medium text-primary">Recommendation:</span>{' '}
                      <span className="text-muted-foreground">
                        Consider consolidating content to the best-performing page, or add canonical
                        tags pointing to the primary URL. Review internal linking to prioritize the main page.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getPathFromUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname || '/';
  } catch {
    return url;
  }
}
